/**
 * @file index.ts
 *
 * @fileoverview The file exports all of the air tracking functions.
 */

import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { Job } from '~/jobiqo/types/types';

const serviceEndpoint = 'https://air.jobiqo.com/campaign-tracking';

type TrackedJob = Pick<Job, 'id' | 'url'> & {
  networkJobId?: string | null;
};

type NetworkSession = {
  url: string;
  sessionId: string;
};

type NetworkDetails = {
  partner: string;
  jobId: string;
};

/**
 * Generates the network session key for a given job ID.
 *
 * @param jobId
 *  The job ID for which to generate the network session key.
 *
 * @returns
 *  The generated network session key as a string.
 */
function networkSessionKey(jobId: number): string {
  return `jobiqo-atnet-${jobId}`;
}

/**
 * Retrieves the network details for a given tracked job.
 *
 * @param job
 *  The tracked job for which to retrieve network details.
 *
 * @returns
 *  The network details if available, otherwise null.
 */
function getNetworkDetails(job: TrackedJob): NetworkDetails | null {
  const partner = getCurrentFrontendConfig().APP.AT_NET?.AT_NET_PARTNER_NAME;
  if (typeof partner !== 'string' || !partner.trim() || !job.networkJobId) {
    // If the partner is not configured or the job has no network job ID, return null.
    return null;
  }
  return {
    partner,
    jobId:
      job.networkJobId === String(job.id)
        ? `${partner}:${job.networkJobId}`
        : job.networkJobId,
  };
}

/**
 * Retrieves the network session for a given tracked job.
 *
 * @param job
 *  The tracked job for which to retrieve the network session.
 *
 * @returns
 *  The network session for the given tracked job.
 */
function getNetworkSession(job: TrackedJob): NetworkSession {
  const stored = sessionStorage.getItem(networkSessionKey(job.id));
  if (stored) {
    try {
      const session = JSON.parse(stored);
      if (session.url && session.sessionId) {
        return session;
      }
    } catch {
      // Replace invalid session data with a new session.
    }
  }

  const url = job.url?.path
    ? new URL(job.url.path, location.origin).href
    : location.href;
  const session = { url, sessionId: getRandomSessionId() };
  sessionStorage.setItem(networkSessionKey(job.id), JSON.stringify(session));
  return session;
}

/**
 * Sends an event to the air tracking service with the provided body and network details.
 *
 * @param body
 *  The body of the event to be sent.
 *
 * @param network
 *  The network details to be included in the event, if available.
 */
function sendEvent(
  body: Record<string, string>,
  network?: NetworkDetails,
): void {
  fetch(serviceEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...body,
      ...(network && {
        network: 'atnet',
        network_partner: network.partner,
        network_job_id: network.jobId,
      }),
    }),
  });
}

/**
 * Stores the air tracking data in the local storage for the
 * next 30 minutes.
 *
 * @param {number} jobId
 *  The job ID to be stored in the local storage.
 * @param {string} url
 *  The URL to be stored in the local storage.
 * @param {string} session_id
 *  The session ID to be stored in the local storage.
 */
function setLocalStorageKey(
  jobId: number,
  url: string,
  session_id: string,
): void {
  localStorage.setItem(
    generateLocalStorageKey(jobId),
    JSON.stringify({
      jobId,
      url,
      session_id,
      timestamp: new Date().getTime(),
    }),
  );
}

/**
 * Checks if the view event has already been sent for the job.
 *
 * @param {number} jobId
 *  The job ID to be checked.
 *
 * @returns {boolean}
 *  Returns true if the view event has already been sent.
 * Otherwise, returns false.
 */
function isViewEventAlreadySent(jobId: number): boolean {
  const localStorageKey = generateLocalStorageKey(jobId);
  const localStorageData = localStorage.getItem(localStorageKey);
  if (!localStorageData) {
    return false;
  }

  const { timestamp } = JSON.parse(localStorageData);

  const timeDifference = new Date().getTime() - timestamp;
  const thirtyMinutes = 30 * 60 * 1000;

  if (timeDifference > thirtyMinutes) {
    localStorage.removeItem(localStorageKey);
  }

  return timeDifference < thirtyMinutes;
}

/**
 * Sends the view event to the air tracking service.
 * @param {TrackedJob} job The job to be sent to the air tracking service.
 * @returns {void}
 */
function postViewEvent(job: TrackedJob): void {
  const network = getNetworkDetails(job);
  if (!network) {
    if (!getCurrentFrontendConfig().APP.JOBIQO_AIR_TRACKING_ACTIVE) {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    if (
      !urlParams.get('aw_campaign_id') &&
      !urlParams.get('air_campaign_item_id')
    ) {
      // If there are no relevant campaign parameters in the URL,
      // do not send the view event.
      return;
    }
  }

  const url = location.href;
  const session_id = getRandomSessionId();
  if (network) {
    // A new detail-page landing starts a new session with its full URL.
    sessionStorage.setItem(
      networkSessionKey(job.id),
      JSON.stringify({ url, sessionId: session_id }),
    );
  } else {
    setLocalStorageKey(job.id, url, session_id);
  }

  sendEvent({ event_type: 'view', url, session_id }, network ?? undefined);
}

/**
 * Sends the apply event to the air tracking service.
 *
 * @param {TrackedJob} job
 *  The job to be sent to the air tracking service.
 *
 * @returns {void}
 */
function postApplyEvent(job: TrackedJob): void {
  const network = getNetworkDetails(job);
  let url: string;
  let session_id: string;

  if (network) {
    // For network jobs, we retrieve the session information
    // from the network session.
    const session = getNetworkSession(job);
    url = session.url;
    session_id = session.sessionId;
  } else {
    // Not a network job detail page,
    // so we rely on local storage for session information.
    if (!getCurrentFrontendConfig().APP.JOBIQO_AIR_TRACKING_ACTIVE) {
      // If AIR tracking is not active, we do not send the apply event.
      return;
    }

    const localStorageItem = localStorage.getItem(
      generateLocalStorageKey(job.id),
    );
    if (!isViewEventAlreadySent(job.id) || !localStorageItem) {
      // If the view event has not been sent or there is no local storage item,
      // we do not send the apply event.
      return;
    }

    ({ url, session_id } = JSON.parse(localStorageItem));
  }

  sendEvent({ event_type: 'apply', url, session_id }, network ?? undefined);
}

/**
 * Generates the local storage key for the job ID.
 *
 * @param {number} jobId
 *  The job ID to be used to generate the local storage key.
 *
 * @returns {string}
 *  Returns the local storage key.
 */
function generateLocalStorageKey(jobId: number): string {
  return `jobiqo-air-${jobId}`;
}

/**
 * Generates a random session ID using the current timestamp.
 *
 * @returns {string}
 *  Returns the random session ID.
 */
function getRandomSessionId(): string {
  return `jobiqo-air-${
    new Date().getTime().toString() + Math.random().toString(36).substr(2, 9)
  }`;
}

export const AirTrackingService = {
  postViewEvent,
  postApplyEvent,
  isViewEventAlreadySent,
  generateLocalStorageKey,
};
